// PushSetupBanner — 알림 권한·앱 설치 안내(둘 다 사용자가 '한 번 눌러야' 되는 것들).
//
// 🔴 권한 요청은 반드시 이 버튼(사용자 제스처) 안에서만 한다 — 페이지 로드에서 부르면 브라우저가
//    무시하거나 자동 거부한다. 자동 재구독(usePushAutoSubscribe)은 granted 일 때만 움직인다.
// 숨기는 경우: 비로그인 / 이미 설치됨 / 권한 denied(브라우저가 다시 묻지 않아 띄워야 소용없다) / 닫음.
// 녹음 중에는 CSS 로만 숨긴다(FloatingRecorder 가 <html data-gw-recording="1"> 을 건다 — 녹음 화면 상태바
// 버튼을 덮지 않게). 녹음 컨텍스트 훅은 여기서 부르지 않는다: Provider 밖에서 던지고, Provider 없이 렌더하는 시험이 있다.
// 🔑 g4-068: 띠가 떠 있는 동안 «자기가 차지한 높이(바닥 거리 + 높이 + 8)»를 <html> 의 `--gw-push-banner-clear` 로 알린다 —
//    채팅 열(ChatPage)이 그만큼 아래 여백을 두어 입력줄이 띠 밑에 깔리지 않게 한다. 띠의 자리·모양은 그대로다.
//    키보드가 열리면(`data-gw-keyboard="1"`) 띠는 CSS 로 숨고(녹음 규칙과 같은 꼴) 변수도 지워진다.
// 🔑 g4-068b: 휴대폰 AI 채팅(`/`·`/chat`)에서는 띠를 아예 그리지 않는다 — 휴대폰은 높이가 모자라 띠 여백만큼 채팅 칸이 줄어 추천 단추가 가려진다(마스터 질문함 #146 가).
//    홈·메신저 등 다른 화면과 데스크톱은 그대로. «닫음»이 아니므로 gwPushSetupDismissed 는 건드리지 않는다(채팅을 떠나면 다시 뜬다).
// 🔑 g2786: 모양은 시안 F `.toast` 를 옮긴 «행동 토스트» — 하단 가운데 · 내용 폭(모바일 상한 280) · 어두운 바탕/밝은 글자(토큰 반전) · 12px.
//    자리(모바일 탭바 위 16 · 데스크톱 바닥 16)·단추·동작은 그대로. 모바일 스티커 시트가 열리면(`data-gw-sheet="1"` · StickerPicker 가 건다)
//    키보드·녹음과 같은 꼴로 CSS 로 숨고 변수도 지워진다.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isInstalledApp, isPushSupported, notificationPermission, subscribeCurrentBrowser } from '../utils/push';
import useIsMobile from '../hooks/useIsMobile';
import { tabbarCalc } from '../lib/tabbarMetrics';

const DISMISS_KEY = 'gwPushSetupDismissed';
const CLEAR_VAR = '--gw-push-banner-clear';   // g4-068 — ChatPage 하단 여백이 읽는 변수(없으면 0px 폴백)

function isIos() {
  try { return /iphone|ipad|ipod/i.test(navigator.userAgent || ''); } catch (e) { return false; }
}

export default function PushSetupBanner() {
  const [perm, setPerm] = useState(notificationPermission);
  const [installEvent, setInstallEvent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [closed, setClosed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  });
  const bannerRef = useRef(null);
  const location = useLocation();   // 로그인/로그아웃 라우트 전환 시 아래 token 재평가 · g4-068b 채팅 경로 판정
  const mobile = useIsMobile();
  // 채팅 경로 = `/`(새 대화 · App.jsx:341 · AppSidebar «새 대화» navigate('/'))·`/chat`(App.jsx:343). 프로젝트 채팅은 `?project=N` 쿼리라 같은 경로다.
  const path = location.pathname.replace(/\/+$/, '') || '/';   // g4-068b — 끝 슬래시(`/chat/`)에도 같은 판정
  const onChat = path === '/' || path === '/chat';

  useEffect(() => {
    // 크로미움은 설치 가능해지면 beforeinstallprompt 를 준다 — 눌러야 쓸 수 있으니 붙잡아 둔다.
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => setInstallEvent(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  let token = '';
  try { token = localStorage.getItem('token') || ''; } catch (e) { token = ''; }
  const installed = isInstalledApp();
  const askNotify = isPushSupported() && perm === 'default';
  const canInstall = !installed && !!installEvent;
  const iosNeedsInstall = !installed && isIos();   // iOS 는 홈 화면에 설치해야 웹푸시가 열린다
  const hidden = !token || closed || (!askNotify && !canInstall && !iosNeedsInstall) || (mobile && onChat);   // g4-068b

  // g4-068 — 띠 높이 알림. 🔴 훅 순서: 조기 return «앞»에 둔다(훅은 조건 없이 매번 불린다) · 그려졌는지는 ref 로 안다.
  //   CSS px 두 값(getComputedStyle().bottom · offsetHeight)만 쓴다 — getBoundingClientRect 는 html zoom 배율 px 라 쓰지 않는다.
  //   offsetHeight 0(display:none — 녹음 중·키보드 열림)이면 변수를 지운다. 그려지지 않거나 언마운트돼도 지운다.
  useEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (hidden || !el) { root.style.removeProperty(CLEAR_VAR); return undefined; }
    const measure = () => {
      const h = el.offsetHeight;
      if (!h) { root.style.removeProperty(CLEAR_VAR); return; }
      const bottom = parseFloat(getComputedStyle(el).bottom) || 0;
      const v = `${Math.ceil(bottom + h + 8)}px`;
      // 같은 값이면 다시 쓰지 않는다 — 감시 목록이 넓어져도 style 속성 변화가 측정을 다시 부르는 고리가 생기지 않게.
      if (root.style.getPropertyValue(CLEAR_VAR) !== v) root.style.setProperty(CLEAR_VAR, v);
    };
    measure();
    // 문구 접힘·msg 줄·display:none 전환 = 크기 변화 / 미디어 쿼리 경계 = 크기는 그대로 바닥 거리만 변화 / 키보드·녹음·스티커 시트(g2786) = <html> 속성.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);   // jsdom 에는 없다 — 한 번 재고 나머지 둘만 건다
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(measure);
    if (mo) mo.observe(root, { attributes: true, attributeFilter: ['data-gw-keyboard', 'data-gw-recording', 'data-gw-sheet'] });
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      if (mo) mo.disconnect();
      root.style.removeProperty(CLEAR_VAR);
    };
  }, [hidden]);

  if (hidden) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* */ }
    setClosed(true);
  };

  const enableNotifications = async () => {
    setBusy(true); setMsg('');
    try {
      const p = await window.Notification.requestPermission();   // 🔴 제스처 안에서만
      setPerm(p);
      if (p !== 'granted') { setMsg('브라우저 설정에서 알림을 허용해 주세요.'); return; }
      await subscribeCurrentBrowser();
      setMsg('알림이 켜졌습니다.');
    } catch (e) {
      setMsg('알림을 켜지 못했습니다. 내 정보에서 다시 시도해 주세요.');
    } finally { setBusy(false); }
  };

  const install = async () => {
    if (!installEvent) return;
    setBusy(true);
    try { installEvent.prompt(); await installEvent.userChoice; } catch (e) { /* */ }
    setInstallEvent(null);
    setBusy(false);
  };

  // 단추 값 = 시안 F `.btn` 을 토스트에 맞춰 한 단계 작게(높이 27 = 줄높이 15 + 위아래 5×2 + 테두리 1×2 · 반경 5 · 12px/500)·`.btn.primary`·`.quiet`.
  //   색은 --gw-* 토큰(라이트 기본값을 폴백으로) — 하드코딩 #2563eb 는 쓰지 않는다(g2-009·g4-054·g5-076).
  //   🔴 :hover 로 바뀌는 속성(바탕·테두리색·글자색)은 인라인에 두지 않는다 — 인라인이 스타일시트 :hover 규칙을 이겨 hover 가 안 보인다.
  //   그 값은 아래 <style> 의 클래스 규칙(기본 + :hover:not(:disabled) + busy 는 :disabled)이 갖고, 인라인은 모양(패딩·반경·글자)만 갖는다.
  const btn = { padding: '5px 10px', borderRadius: '5px', borderWidth: '1px', borderStyle: 'solid', fontSize: '12px', fontWeight: 500, lineHeight: '15px', cursor: busy ? 'not-allowed' : 'pointer' };
  // g2786 — 작은 토스트라 문구를 짧게(모바일 폭 280 안에서 안내문 한 줄 · 단추는 다음 줄).
  const text = askNotify
    ? '업무 알림(DM·멘션)을 받으시겠습니까?'
    : (canInstall
      ? '앱으로 설치하면 더 빠르게 열립니다.'
      : '공유 → ‘홈 화면에 추가’ 후 알림을 켜 주세요.');

  return (
    <div className="gw-push-banner" ref={bannerRef} role="region" aria-label="알림·앱 설치 안내">
      {/* g2786 — 가운데는 left:50% + translateX(-50%) · 폭 상한은 100vw 가 아니라 100%(모바일 배율에서 레이아웃 폭 기준이라 여백이 남는다).
          모바일 폭 상한은 기본 규칙 «뒤»의 @media 한 줄(바닥 거리 @media 줄은 글자 그대로 따로 둔다). */}
      <style>{`
        .gw-push-banner{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(16px + env(safe-area-inset-bottom));
          z-index:990;width:max-content;max-width:min(480px, calc(100% - 40px));display:flex;flex-wrap:wrap;align-items:center;
          justify-content:flex-end;gap:4px 8px;padding:8px 8px 8px 16px;background:var(--gw-text,#32312e);color:var(--gw-bg,#fff);
          border:0;border-radius:7px;font-size:12px;line-height:1.6;
          box-shadow:var(--gw-float-shadow,0 4px 24px #1c191711,0 1px 3px #1c19170b);box-sizing:border-box;}
        @media (max-width:768px){.gw-push-banner{max-width:min(280px, calc(100% - 40px));}}
        .gw-push-banner .gw-push-primary{background:var(--gw-blue,#2383e2);border-color:var(--gw-blue,#2383e2);color:#fff;box-shadow:0 1px 1px var(--gw-line,#eeeeec)}
        .gw-push-banner .gw-push-primary:hover:not(:disabled){background:var(--gw-blue-hover,#1873ce);border-color:var(--gw-blue-hover,#1873ce)}
        .gw-push-banner .gw-push-primary:disabled{background:var(--gw-gray-soft,#f1f1ef);border-color:transparent;color:var(--gw-muted,#92918d);box-shadow:none}
        .gw-push-banner .gw-push-secondary{background:transparent;border-color:color-mix(in srgb, var(--gw-bg,#fff) 35%, transparent);color:var(--gw-bg,#fff);box-shadow:none}
        .gw-push-banner .gw-push-secondary:hover:not(:disabled){background:color-mix(in srgb, var(--gw-bg,#fff) 14%, transparent)}
        .gw-push-banner .gw-push-quiet{background:transparent;border-color:transparent;color:color-mix(in srgb, var(--gw-bg,#fff) 72%, transparent)}
        .gw-push-banner .gw-push-quiet:hover:not(:disabled){background:color-mix(in srgb, var(--gw-bg,#fff) 14%, transparent);color:var(--gw-bg,#fff)}
        @media (max-width:768px){.gw-push-banner{bottom:${tabbarCalc(16)};}}
        html[data-gw-recording="1"] .gw-push-banner{display:none}
        html[data-gw-keyboard="1"] .gw-push-banner{display:none}
        html[data-gw-sheet="1"] .gw-push-banner{display:none}
      `}</style>
      <span style={{ fontSize: '12px', color: 'inherit', flex: '1 1 auto', minWidth: 0, lineHeight: 1.6 }}>
        {text}
        {msg && <b style={{ display: 'block', marginTop: '2px', color: 'inherit' }}>{msg}</b>}
      </span>
      {/* g2786 — 단추는 한 묶음(div · span 아님 — 시험이 첫 span 을 안내문으로 집는다): 단추 조합·«처리 중…»이 바뀌어도 줄이 갈리거나 누른 단추가 자리를 옮기지 않는다. */}
      <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
        {askNotify && (
          <button type="button" className="gw-push-primary" onClick={enableNotifications} disabled={busy}
            style={btn}>
            {busy ? '처리 중…' : '업무 알림 받기'}
          </button>
        )}
        {canInstall && (
          <button type="button" className="gw-push-secondary" onClick={install} disabled={busy}
            style={btn}>
            앱으로 설치
          </button>
        )}
        <button type="button" className="gw-push-quiet" onClick={dismiss} aria-label="안내 닫기"
          style={{ ...btn, padding: '5px 8px', fontWeight: 400 }}>
          닫기
        </button>
      </div>
    </div>
  );
}
